import React, { useEffect, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Grid,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Switch from "@mui/material/Switch";
import { Add, Delete } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/it";
import { AutocompleteCst } from "@components/InputCst";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { useTranslation } from "react-i18next";
import {
  AddButton,
  Divider,
  ExcludeWeekendsToggleContainer,
  MainContainer,
  SectionTitle,
  SingleSlotContainer,
  SingleSlotContent,
  SlotsGridItem,
} from "@components/TimeSlotCst/style";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import dayjs from "dayjs";

export interface TimeSlot {
  slot_start?: string;
  slot_end?: string;
  exclude_weekend: boolean;
  apply_user_timezone: boolean;
  timezone_id?: number;
}

interface Timezone {
  id: number;
  zone_id: string;
  label: string;
}

interface TimeSlotCstProps {
  initialTimeSlots: TimeSlot[];
  onChange: (newRange: TimeSlot[]) => void;
}

const TimeSlotCst = ({ initialTimeSlots, onChange }: TimeSlotCstProps) => {
  const { t } = useTranslation("activities-page");
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showInvalidDatesMessage, setShowInvalidDatesMessage] = useState(false);
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>(
    initialTimeSlots || [],
  );

  const [timeSlotData, setTimeSlotData] = useState<TimeSlot>({
    slot_start: "",
    slot_end: "",
    exclude_weekend: false,
    apply_user_timezone: false,
    timezone_id: undefined,
  });

  const { dataDetail: catalogTimeZones } = useFetchDataDetail(
    `${ServicesURL.catalogTimeZones}`,
  );

  const timezoneOptionsList: Record<string, any>[] = catalogTimeZones
    ? catalogTimeZones.map((timeZone: Timezone) => ({
        label: timeZone.label,
        value: timeZone.id,
      }))
    : [];

  const addSlot = () => {
    if (!validateFields()) return;

    const updatedRangeList = [
      ...timeSlots,
      {
        ...timeSlotData,
        slot_start: dayjs(timeSlotData.slot_start).format("HH:mm"),
        slot_end: dayjs(timeSlotData.slot_end).format("HH:mm"),
      },
    ];

    setTimeSlotData({
      slot_start: "",
      slot_end: "",
      exclude_weekend: false,
      apply_user_timezone: false,
      timezone_id: undefined,
    });

    setTimeSlots(updatedRangeList);
    onChange(updatedRangeList);
  };

  const delRange = (selectedIndex: number) => {
    const updatedRangeList = [...timeSlots];
    updatedRangeList.splice(selectedIndex, 1);
    setTimeSlots(updatedRangeList);
    onChange(updatedRangeList);
  };

  const validateFields = () => {
    setValidationErrors([]);
    const missingFieldKeys: string[] = [];
    if (!timeSlotData.slot_start || !dayjs(timeSlotData.slot_start).isValid())
      missingFieldKeys.push("slot_start");
    if (!timeSlotData.slot_end || !dayjs(timeSlotData.slot_end).isValid())
      missingFieldKeys.push("slot_end");
    if (!timeSlotData.apply_user_timezone && !timeSlotData.timezone_id)
      missingFieldKeys.push("timezone_id");

    const slotStart = dayjs(timeSlotData.slot_start);
    const slotEnd = dayjs(timeSlotData.slot_end);

    if (slotEnd.isBefore(slotStart)) {
      missingFieldKeys.push("slot_start");
      missingFieldKeys.push("slot_end");
      setShowInvalidDatesMessage(true);
    } else {
      setShowInvalidDatesMessage(false);
    }

    setValidationErrors(missingFieldKeys);
    return !missingFieldKeys || missingFieldKeys.length === 0;
  };

  const onTimeZoneChange = (selectedOption?: Record<string, any>) => {
    setTimeSlotData((current) => ({
      ...current,
      timezone_id:
        selectedOption && selectedOption.value
          ? catalogTimeZones.find(
              (tz: Timezone) => tz.id === selectedOption?.value,
            ).id
          : undefined,
    }));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={"it"}>
      <MainContainer theme={theme}>
        <SectionTitle theme={theme}>{t("time-slots.title")}</SectionTitle>
        <Grid container spacing={"24px"}>
          {showInvalidDatesMessage && (
            <Grid item xs={12}>
              <Typography sx={{ color: theme.palette.error.main }}>
                {t("time-slots.dates-error")}
              </Typography>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <TimePicker
              label={t("time-slots.from-hour-label") || ""}
              slotProps={{
                textField: {
                  size: "small",
                  error: validationErrors.includes("slot_start"),
                  required: true,
                },
                layout: {
                  sx: {
                    ul: {
                      "&::-webkit-scrollbar": {
                        width: "6px",
                        background: alpha(theme.palette.text.primary, 0.1),
                      },
                      "::-webkit-scrollbar-thumb": {
                        borderRadius: 6,
                        background: alpha(theme.palette.text.primary, 0.2),
                      },
                      "::-webkit-scrollbar-thumb:hover": {
                        background: alpha(theme.palette.text.primary, 0.4),
                      },
                    },
                  },
                },
              }}
              sx={{
                width: "100%",
              }}
              onChange={(value: any) =>
                setTimeSlotData((current) => ({
                  ...current,
                  slot_start: value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TimePicker
              label={t("time-slots.to-hour-label") || ""}
              slotProps={{
                textField: {
                  size: "small",
                  error: validationErrors.includes("slot_end"),
                  required: true,
                },
                layout: {
                  sx: {
                    ul: {
                      "&::-webkit-scrollbar": {
                        width: "6px",
                        background: alpha(theme.palette.text.primary, 0.1),
                      },
                      "::-webkit-scrollbar-thumb": {
                        borderRadius: 6,
                        background: alpha(theme.palette.text.primary, 0.2),
                      },
                      "::-webkit-scrollbar-thumb:hover": {
                        background: alpha(theme.palette.text.primary, 0.4),
                      },
                    },
                  },
                },
              }}
              sx={{
                width: "100%",
              }}
              onChange={(value: any) =>
                setTimeSlotData((current) => ({
                  ...current,
                  slot_end: value,
                }))
              }
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <AutocompleteCst
              id={"timezone_id"}
              label={t("time-slots.timezone-label") || ""}
              value={timeSlotData.timezone_id}
              required={!timeSlotData.apply_user_timezone}
              onChange={onTimeZoneChange}
              options={timezoneOptionsList}
              disabled={timeSlotData.apply_user_timezone}
              error={validationErrors.includes("timezone_id")}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <ExcludeWeekendsToggleContainer gap={2}>
              <Typography>{t("time-slots.exclude-weekends-toggle")}</Typography>
              <Switch
                checked={timeSlotData.exclude_weekend}
                onChange={(_, checked) =>
                  setTimeSlotData((current) => ({
                    ...current,
                    exclude_weekend: checked,
                  }))
                }
              />
            </ExcludeWeekendsToggleContainer>
          </Grid>
          <Grid item xs={12} md={4}>
            <ExcludeWeekendsToggleContainer gap={2}>
              <Typography>
                {t("time-slots.apply-user-timezone-toggle")}
              </Typography>
              <Switch
                checked={timeSlotData.apply_user_timezone}
                onChange={(_, checked) =>
                  setTimeSlotData((current) => ({
                    ...current,
                    apply_user_timezone: checked,
                    timezone_id: undefined,
                  }))
                }
              />
            </ExcludeWeekendsToggleContainer>
          </Grid>
          <Grid item xs={12}>
            <AddButton theme={theme} onClick={addSlot}>
              <Add color={"primary"} />
              <Typography sx={{ color: theme.palette.primary.main }}>
                {t("time-slots.add-slot-btn")}
              </Typography>
            </AddButton>
          </Grid>
          {timeSlots && timeSlots.length > 0 && (
            <Grid item xs={12}>
              <SlotsGridItem gap={2}>
                <Divider theme={theme} />

                {timeSlots.map((range, rangeIndex) => {
                  const timezone: Timezone = catalogTimeZones.find(
                    (tz: Timezone) => tz.id === range.timezone_id,
                  );

                  return (
                    <SingleSlotContainer
                      theme={theme}
                      key={`range_${rangeIndex}_${range.slot_start}_${range.slot_end}`}
                    >
                      {!isSmallScreen && (
                        <IconButton
                          color={"primary"}
                          onClick={() => delRange(rangeIndex)}
                        >
                          <Delete />
                        </IconButton>
                      )}
                      <SingleSlotContent
                        gap={isSmallScreen ? "10px" : "5px"}
                        theme={theme}
                      >
                        <Typography>
                          {t("time-slots.row.hours-label", {
                            from: range.slot_start,
                            to: range.slot_end,
                          })}
                        </Typography>
                        {timezone && (
                          <Typography>
                            {t("time-slots.row.timezone-label")}{" "}
                            {timezone.label} ({timezone.zone_id})
                          </Typography>
                        )}
                        <Typography>
                          {t("time-slots.row.exclude-weekends-label")}{" "}
                          <span
                            style={{
                              color: range.exclude_weekend
                                ? theme.palette.success.main
                                : theme.palette.primary.main,
                            }}
                          >
                            {range.exclude_weekend
                              ? t("time-slots.row.yes")
                              : t("time-slots.row.no")}
                          </span>
                        </Typography>
                        <Typography>
                          {t("time-slots.row.apply-user-timezone-label")}{" "}
                          <span
                            style={{
                              color: range.apply_user_timezone
                                ? theme.palette.success.main
                                : theme.palette.primary.main,
                            }}
                          >
                            {range.apply_user_timezone
                              ? t("time-slots.row.yes")
                              : t("time-slots.row.no")}
                          </span>
                        </Typography>
                        {isSmallScreen && (
                          <Button
                            onClick={() => delRange(rangeIndex)}
                            startIcon={<Delete />}
                            color={"primary"}
                            variant={"contained"}
                          >
                            {t("time-slots.row.delete-btn")}
                          </Button>
                        )}
                      </SingleSlotContent>
                    </SingleSlotContainer>
                  );
                })}
              </SlotsGridItem>
            </Grid>
          )}
        </Grid>
      </MainContainer>
    </LocalizationProvider>
  );
};

export default TimeSlotCst;
